import AsyncSelectComponent from '@components/inputs/asyncSelect/AsyncSelect.component';
import AsyncSelectWithSearchComponent from '@components/inputs/asyncSelect/AsyncSelectWithSearch.component';
import SelectComponent, { SelectItem } from '@components/inputs/select/Select.component';
import TextFieldComponent from '@components/inputs/textField/TextField.component';
import ModalComponent from '@components/modal/Modal.component';
import { Colors } from '@constants/colors.constants';
import { Locales } from '@constants/locales.constants';
import { Sizes } from '@constants/sizes.constants';
import { Button, Divider, Stack, Typography } from '@mui/material';
import { Audit, CreateAuditDto, UpdateAuditDto } from '@store/audits/audits.model';
import { auditsService } from '@store/audits/audits.service';
import { templatesService } from '@store/audits/templates/templates.service';
import { clientsService } from '@store/clients';
import { clientsUnitsService } from '@store/clients/units';
import { useSnackbar } from 'notistack';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { map } from 'rxjs';

interface NewAuditModalProps {
    handleClose: () => void;
    isUpdate?: boolean;
    audit?: Audit;
}

const NewAuditModal = (props: NewAuditModalProps) => {
    const { t } = useTranslation();
    const { handleClose, isUpdate, audit } = props;
    const { enqueueSnackbar } = useSnackbar();

    const [name, setName] = useState('');

    const [client, setClient] = useState<SelectItem>();
    const [clientUnits, setClientUnits] = useState<SelectItem>();
    const [template, setTemplate] = useState<SelectItem>();
    const [currentLanguage, setCurrentLanguage] = useState<SelectItem>(Locales.selectItems()[1]);


    function handleChangeClient(client: any) {
        setClient(client);
        setClientUnits(undefined);
    }

    function handleChangeClientUnit(clientUnit: any) {
        setClientUnits(clientUnit);
    }

    function handleChangeName(name: any) {
        setName(name);
    }

    function postAudit() {
        const newAudit: CreateAuditDto = {
            name: name,
            clientOrganisationUnitId: clientUnits?.value,
            defaultLanguage: (currentLanguage.value as string).toLocaleUpperCase() as Locales
        };

        if (template && template?.value != '')
            newAudit['auditTemplateId'] = template.value;

        auditsService.createAudit(newAudit).pipe()
            .subscribe({
                next: () => {
                    enqueueSnackbar(t('audits.success.create'), { variant: 'success' });
                    handleClose();
                },
                error: (err) => enqueueSnackbar(err.text, err.options),
            });

    }

    useEffect(() => {
        if (audit?.name) {
            setName(audit.name);
        }
        if (audit?.clientOrganisationUnit) {
            setClientUnits({ label: audit.clientOrganisationUnit, value: audit.clientOrganisationUnitId });
        }
        if (audit?.clientOrganisation) {
            setClient({ label: audit.clientOrganisation, value: audit.clientOrganisationId });
        }
    }, [audit?.clientOrganisation, audit?.clientOrganisationId, audit?.clientOrganisationUnit, audit?.clientOrganisationUnitId, audit?.name]);

    function patchAudit() {
        const body: UpdateAuditDto = {};

        if (name) body.name = name;
        if (clientUnits?.value) body.clientOrganisationUnitId = clientUnits.value;

        auditsService.updateAudit(body, audit!.id).pipe()
            .subscribe({
                next: () => {
                    enqueueSnackbar(t('audits.success.update'), { variant: 'success' });
                    handleClose();
                },
                error: (err) => enqueueSnackbar(err.text, err.options),
            });
    }

    return (
        <ModalComponent
            handleClose={handleClose}
            title={isUpdate ? t('audits.modals.updateAudit.title') : t('audits.modals.newAudit.title')}
            content={(
                <Stack width="70%" spacing={3} pt="55px" pb="10px">
                    <TextFieldComponent
                        color="secondary"
                        labelWidth={160}
                        handleChange={handleChangeName}
                        label={t('audits.modals.newAudit.titleAudit')}
                        placeholder={t('audits.modals.newAudit.titleAudit')}
                        value={name}
                    />
                    {/*<SelectComponent*/}
                    {/*  color="secondary"*/}
                    {/*  labelWidth={160}*/}
                    {/*  label={t('audits.modals.newAudit.Template')}*/}
                    {/*  placeholder={t('audits.modals.newAudit.placeholder')}*/}
                    {/*  value={''}*/}
                    {/*  items={[]}*/}
                    {/*/>*/}
                    {!isUpdate && (
                        <AsyncSelectComponent
                            labelWidth={160}
                            label={t('audits.modals.newAudit.Template')}
                            placeholder={t('audits.modals.newAudit.placeholder')}
                            getOptions={() => templatesService.getTemplates().pipe(
                                map((options) => options.data.map((o) => ({ label: o.name || '-', value: o.id }))))}
                            value={template}
                            color='secondary'
                            handleChange={(data) => setTemplate(data)}
                        />
                    )}

                    <Divider sx={{ border: 'solid 1px RGBA(134, 143, 156, 0.22)' }} />
                    <Typography sx={{ alignSelf: 'center' }} fontWeight={700} fontSize="16px" color={Colors.black}>
                        {t('audits.modals.newAudit.clientTitle')}
                    </Typography>
                    <AsyncSelectWithSearchComponent
                        color="secondary"
                        labelWidth={Sizes.drawerInputLabelWidth}
                        label={t('audits.modals.newAudit.compagny')}
                        placeholder={t('audits.modals.newAudit.placeholder')}
                        handleChange={handleChangeClient}
                        value={client ?? undefined}
                        getOptions={(search) => {
                            return clientsService.searchClients(search).pipe(
                                map((options) => options.data.map((o) => ({ label: o.name || '-', value: o.id })))
                            );
                        }} />
                    <AsyncSelectWithSearchComponent
                        color="secondary"
                        labelWidth={Sizes.drawerInputLabelWidth}
                        label={t('audits.modals.newAudit.auditSite')}
                        placeholder={t('audits.modals.newAudit.placeholder')}
                        handleChange={handleChangeClientUnit}
                        value={clientUnits ?? undefined}
                        getOptions={(search) => {
                            return clientsUnitsService.searchClientsUnits(search, client?.value ? [client.value] : undefined).pipe(
                                map((options) => options.data.map((o) => ({ label: o.name || '-', value: o.id })))
                            );
                        }} />
                    <Divider sx={{ border: 'solid 1px RGBA(134, 143, 156, 0.22)' }} />
                    <SelectComponent
                        color="primary"
                        size="small"
                        label='Langue'
                        labelWidth={Sizes.drawerInputLabelWidth}
                        inputWidth={60}
                        items={Locales.selectItems()}
                        value={currentLanguage.value}
                        handleChange={(value) => setCurrentLanguage(Locales.selectItems().find((l) => l.value === value) as SelectItem)}
                        required
                    />
                </Stack>
            )}
            actions={(
                <Button
                    variant="contained"
                    color="primary"
                    onClick={isUpdate ? patchAudit : postAudit}>
                    {isUpdate ? t('global.update') : t('global.add')}
                </Button>
            )} />
    );
};

export default NewAuditModal;
